/**
 * A gate's ask, drawn: what happened, in order, and what the editor is asked.
 *
 * The side that opened the gate writes the ask (`payload.ask`) from what it
 * recorded: a build's checks, the stage that stopped short, the error class
 * behind a failed collection. This component only draws it. It never composes
 * a fact of its own, and a gate without an ask renders exactly as before.
 *
 * Two mounts, one component: the dock shows the first facts beside the
 * decision, and the stop card at the top of the case file ("Why Wright
 * stopped") shows all of them with their output, so the dock stays short and
 * the reading is there for whoever wants it.
 */
import { useState } from "react";
import {
  readAskLink,
  readGateAsk,
  STOP_CLASS_LABEL,
  type GateAsk,
  type GateAskFact,
  type GateAskLink,
  type WorkItemGate,
} from "@yagni/shared";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ASK_JUMP,
  ASK_UNRECORDED_LINE,
  ASK_OUTPUT_HIDE,
  ASK_OUTPUT_SHOW,
  askMoreFacts,
  askNeedLead,
  viewPrLabel,
  askStoppedLabel,
} from "./workCopy.js";

/**
 * The gate's ask, when it carries a readable one. Total: any other shape is
 * null. The reader is shared (`readGateAsk`), so the item rail reads the same
 * ask, and the same hidden verbs, the dock does.
 */
export function gateAskOf(gate: WorkItemGate | null | undefined): GateAsk | null {
  return readGateAsk(gate?.payload);
}

const MARK: Record<GateAskFact["tone"], string> = { ok: "✓", bad: "✗", info: "·" };

function Fact({ fact, showDetail }: { fact: GateAskFact; showDetail: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={`wk-ask-fact is-${fact.tone}`} data-testid="ask-fact">
      <span className="wk-ask-mark" aria-hidden>
        {MARK[fact.tone]}
      </span>
      <div className="wk-ask-body">
        {fact.href ? (
          <a
            className="wk-ask-text wk-ask-link"
            href={fact.href}
            target="_blank"
            rel="noreferrer"
            data-testid="ask-fact-link"
          >
            {fact.text}
            <ExternalLink width={11} height={11} aria-hidden />
          </a>
        ) : (
          <span className="wk-ask-text">{fact.text}</span>
        )}
        {fact.excerpt && (
          <button
            type="button"
            className="wk-ask-more"
            aria-expanded={open}
            data-testid="ask-fact-output"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? ASK_OUTPUT_HIDE : `${ASK_OUTPUT_SHOW} ▸`}
          </button>
        )}
        {showDetail && fact.detail && <span className="wk-ask-detail">{fact.detail}</span>}
        {open && fact.excerpt && (
          <pre className="wk-ask-excerpt" data-testid="ask-fact-excerpt">
            {fact.excerpt}
          </pre>
        )}
      </div>
    </li>
  );
}

/**
 * The facts as a list. `limit` keeps the dock short and says how many more
 * are in the card; `showDetail` prints each fact's second clause (the
 * commands a check ran), which the card has room for and the dock does not.
 */
export function AskFacts({
  facts,
  limit,
  showDetail = false,
  name,
}: {
  facts: readonly GateAskFact[];
  limit?: number;
  showDetail?: boolean;
  /** Whose run stopped, so the "more above" line names the card it points at. */
  name?: string | null;
}) {
  if (facts.length === 0) return null;
  const shown = limit === undefined ? facts : facts.slice(0, limit);
  const hidden = facts.length - shown.length;
  return (
    <div className="wk-ask-facts-wrap">
      <ul className="wk-ask-facts" data-testid="ask-facts">
        {shown.map((fact, index) => (
          <Fact key={`${index}:${fact.text}`} fact={fact} showDetail={showDetail} />
        ))}
      </ul>
      {hidden > 0 && <p className="wk-ask-hidden">{askMoreFacts(hidden, name)}</p>}
    </div>
  );
}

/**
 * The class a stop is (decision 5, 2026-10-19): "Needs you", or "Stopped on
 * our side", with the one plain sentence. The precise code stays on the gate
 * row and in the logs; this is all a customer reads of it. Nothing for an ask
 * written before the classes.
 */
export function AskClass({ ask }: { ask: GateAsk }) {
  if (!ask.stopClass) return null;
  return (
    <p className={`wk-ask-class is-${ask.stopClass}`} data-testid="ask-class">
      <span className="wk-ask-class-label">{STOP_CLASS_LABEL[ask.stopClass]}</span>
      {ask.sentence ? <> · {ask.sentence}</> : null}
    </p>
  );
}

/** "Wright needs: …", the one sentence the ask is for. */
export function AskNeed({ ask, name }: { ask: GateAsk; name?: string | null }) {
  return (
    <p className="wk-ask-need" data-testid="ask-need">
      <span className="wk-ask-need-lead">{askNeedLead(name)}:</span> {ask.need}
    </p>
  );
}

/**
 * Where the reader fixes what stopped the build (an environment, Connections),
 * under the need sentence. An app path only: the shared reader refuses any
 * other, so a stored payload never points the reader off the app.
 */
export function AskLink({ link }: { link: GateAskLink | null | undefined }) {
  const read = readAskLink(link);
  if (!read) return null;
  return (
    <p className="wk-ask-link" data-testid="ask-link">
      <Link to={read.href}>{read.label}</Link>
    </p>
  );
}

/**
 * The way out of an ask about a pull request: the pull request itself, in a
 * new tab. Nothing when the item has no pull request yet. `prUrl` is the
 * server's copy of GitHub's `html_url` (`tickets.pr_url`), the same value the
 * header chip and the More menu already open; a fact's `href` is guarded
 * instead because it rides in a gate payload any writer can shape.
 */
export function AskPrLink({
  prUrl,
  prNumber,
}: {
  prUrl: string | null | undefined;
  prNumber?: number | null;
}) {
  if (!prUrl) return null;
  return (
    <a
      className="wk-ask-pr"
      href={prUrl}
      target="_blank"
      rel="noreferrer"
      data-testid="ask-pr-link"
    >
      {viewPrLabel(prNumber)}
      <ExternalLink width={11} height={11} aria-hidden />
    </a>
  );
}

/** The stop card's right-hand meta: which attempt, and when it stopped. */
function StopMeta({ meta }: { meta?: string | null }) {
  return meta ? <span className="cf-sect-meta">{meta}</span> : null;
}

/**
 * The card at the top of the case file: why the run stopped, everything it
 * recorded, with output, and one way down to the decision. Nothing scrolls on
 * its own; the reader chooses to jump.
 */
export function AskHappenedCard({
  ask,
  name,
  meta,
  onJump,
}: {
  ask: GateAsk;
  name?: string | null;
  /** "attempt 3 · 13:16", when the paused run is known. */
  meta?: string | null;
  onJump?: () => void;
}) {
  return (
    <section className="cf-section wk-ask-card" data-testid="ask-happened">
      <div className="cf-sect">
        {askStoppedLabel(name)}
        <StopMeta meta={meta ?? null} />
      </div>
      <div className="wk-ask-card-body">
        <AskClass ask={ask} />
        <p className="wk-ask-headline">{ask.headline}</p>
        <AskFacts facts={ask.facts} showDetail name={name ?? null} />
        <div className="wk-ask-card-foot">
          {/* A stop that was ours asks nobody for anything. */}
          {ask.stopClass !== "infra" && <AskNeed ask={ask} name={name ?? null} />}
          {onJump && (
            <button type="button" className="wk-ask-jump" data-testid="ask-jump" onClick={onJump}>
              {ASK_JUMP}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The same card for a paused run whose gate predates the ask: the gate's own
 * title, and the honest sentence that the run recorded nothing more. The
 * attempt below opens by default in this case, because it is the only account
 * of what happened.
 */
export function StopFallbackCard({
  title,
  name,
  meta,
}: {
  title: string;
  name?: string | null;
  meta?: string | null;
}) {
  return (
    <section className="cf-section wk-ask-card is-unrecorded" data-testid="ask-unrecorded">
      <div className="cf-sect">
        {askStoppedLabel(name)}
        <StopMeta meta={meta ?? null} />
      </div>
      <div className="wk-ask-card-body">
        <p className="wk-ask-headline">{title}</p>
        <p className="wk-ask-unrecorded">{ASK_UNRECORDED_LINE}</p>
      </div>
    </section>
  );
}
